import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, ArrowRight, Home as HomeIcon } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { api } from "../lib/api";
import { SEO } from "../components/SEO";
import { PageHeader } from "../components/PageHeader";
import { Reveal, Stagger, StaggerItem } from "../components/motion/Reveal";
import { CTASection } from "../components/home/FinancialBenefits";

const DEFAULT_IMAGES = [
  "https://images.unsplash.com/photo-1518780664697-55e3ad937233?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  "https://images.unsplash.com/photo-1600566753086-00f18fb6b3ea?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
];
const DEFAULT_HERO = "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";

export default function VillaConstruction() {
  const { t, lang } = useLang();
  const v = t.villa;
  const [cms, setCms] = useState(null);

  useEffect(() => {
    api.get("/content/villa-page").then((r) => setCms(r.data)).catch(() => {});
  }, []);

  const L = (obj, def) => {
    const val = obj?.[lang] ?? obj?.tr ?? obj?.en;
    return typeof val === "string" && val.trim() ? val : def;
  };
  const LA = (obj, def) => (Array.isArray(obj?.[lang]) && obj[lang].length ? obj[lang] : def);

  const types = v.types.map((def, i) => {
    const c = cms?.types?.[i] || {};
    return {
      name: L(c.name, def.name),
      tagline: L(c.tagline, def.tagline),
      price: L(c.price, def.price),
      desc: L(c.desc, def.desc),
      features: LA(c.features, def.features),
      image: c.image || DEFAULT_IMAGES[i],
    };
  });

  return (
    <>
      <SEO pageKey="villa-construction" title={t.seo.villa.title} description={t.seo.villa.description} />
      <PageHeader pageKey="villa-construction"
        overline={L(cms?.overline, v.overline)}
        title={L(cms?.title, v.title)}
        sub={L(cms?.sub, v.sub)}
        image={cms?.hero_image || DEFAULT_HERO}
        overrideProfile={!!(cms && Object.keys(cms).length)}
      />

      <section className="py-10 sm:py-14 bg-white">
        <div className="max-w-[900px] mx-auto px-5 sm:px-8 text-center">
          <Reveal><p className="text-charcoal/60 text-lg leading-relaxed">{L(cms?.intro, v.intro)}</p></Reveal>
        </div>
      </section>

      <section className="pb-24 bg-white">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
          <Stagger className="grid grid-cols-2 md:grid-cols-3 gap-0">
            {types.map((type, i) => (
              <StaggerItem key={i}>
                <div className="group bg-white overflow-hidden border border-border hover:shadow-2xl transition-shadow duration-500 h-full flex flex-col" data-testid={`villa-type-${i}`}>
                  <div className="aspect-[4/3] overflow-hidden hover-zoom relative">
                    <img src={type.image} alt={type.name} className="w-full h-full object-cover" loading="lazy" />
                    <span className="absolute top-2 start-2 sm:top-4 sm:start-4 bg-gold text-charcoal text-[10px] sm:text-sm font-bold px-2.5 py-1 sm:px-4 sm:py-1.5 rounded-full">{type.price}</span>
                  </div>
                  <div className="p-3.5 sm:p-7 flex-1 flex flex-col">
                    <div className="flex items-center gap-1.5 text-gold text-[9px] sm:text-xs uppercase tracking-wider mb-1.5">
                      <HomeIcon className="w-3 h-3 sm:w-4 sm:h-4" /> {type.tagline}
                    </div>
                    <h3 className="font-display text-base sm:text-2xl text-charcoal mb-2 sm:mb-3 leading-tight">{type.name}</h3>
                    <p className="text-charcoal/55 text-[11px] sm:text-sm leading-snug sm:leading-relaxed mb-3 sm:mb-5">{type.desc}</p>
                    <ul className="space-y-1.5 sm:space-y-2.5 mb-4 sm:mb-7 flex-1">
                      {type.features.map((f, j) => (
                        <li key={j} className="flex items-start gap-1.5 sm:gap-2 text-charcoal/70 text-[11px] sm:text-sm"><Check className="w-3 h-3 sm:w-4 sm:h-4 text-green-light mt-0.5 shrink-0" /> {f}</li>
                      ))}
                    </ul>
                    <Link to="/contact" data-testid={`villa-cta-${i}`} className="group/btn inline-flex items-center justify-center gap-2 rounded-full bg-green text-white font-semibold text-xs sm:text-base px-4 py-2.5 sm:px-6 sm:py-3.5 hover:bg-green-deep transition-colors">
                      {v.choose}
                      <ArrowRight className="w-3.5 h-3.5 sm:w-4 sm:h-4 group-hover/btn:translate-x-1 transition-transform rtl:rotate-180" />
                    </Link>
                  </div>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>
      <CTASection />
    </>
  );
}
